import { type ActionEvent, ACTIONS } from '@krishanmadan/core/src/model/actions';
import { actionEvent } from '@krishanmadan/core/src/events';
import { getStaticFirstMessage } from '@krishanmadan/core/src/model/const';
import { useLocation } from 'react-router';
import { useEffect } from 'react';
import { uuidv4 } from '@epihub/core/src/fn/uuidv4';
import { safely } from '@epihub/core/src/fn/safely';
import { useGlobalStore } from '../store/useGlobalStore';
import { LEGACY_THEMES } from '../theme/theme';
import { useWSSubscription } from '../_ctx/wsClient';
import { log } from '../_ctx/log';
import { downloadCVProgrammatically } from '../lib/downloadCVProgrammatically';
import { HTMLConst } from '../lib/HTMLConst';

export const ACTION_HANDlERS: {
  [K in keyof typeof ACTIONS]: (payload: (typeof ACTIONS[K])['_']['Payload']) => void;
} = {
  updateSoundAction: (payload) => {
    useGlobalStore.getState().settings.updateSoundOn(payload.soundOn);
  },
  changeLanguageAction: (payload) => {
    useGlobalStore.getState().settings.updateLanguage(payload.language);
    useGlobalStore.getState().conversation.updateConversation((prev) => {
      const next = [...prev.messages];
      next[0] = getStaticFirstMessage(payload.language);
      return {
        messages: next,
      };
    });
  },
  changeThemeAction: (payload) => {
    const themeObj = LEGACY_THEMES[payload.theme] ?? LEGACY_THEMES.start;
    useGlobalStore.getState().theme.updateTheme((prev) => {
      prev.legacy = themeObj;
    });
  },
  openCalendarAction: (payload) => {
    useGlobalStore.getState().settings.setCalendarDrawerOpen(true);
  },
  setQuickResponsesAction: (payload) => {
    useGlobalStore.getState().settings.setQuickResponses(payload.quickResponses);
  },
  updateUsersNameAction: (payload) => {
    useGlobalStore.getState().settings.setVisitorName(payload.name);
  },
  flagMisuseRate: (payload) => {
    useGlobalStore.getState().settings.accumulateMisuseRate(payload.misuseRate);
  },
  downloadCV: (payload) => {
    downloadCVProgrammatically();
  },
  autoMessage: ({ text }) => {
    useGlobalStore.getState().conversation.setChatInput(text);
    // Wait for React to re-render with the new input value before clicking send
    setTimeout(() => {
      safely(() => {
        const button = document.getElementById(HTMLConst.sendButtonTarget);
        button?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        button?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      });
    }, 0);
  },
};

const handledActionIds = new Set<string>();

export function handleActionEvent(event: ActionEvent) {
  const handler = ACTION_HANDlERS[event.type];
  if (!handler) {
    log.w('handleActionEvent', `unknown action type ${event.type} (no ${event.type} key in ACTION_HANDlERS)`);
    return;
  }

  if (handledActionIds.has(event.actionId)) {
    log.i('handleActionEvent', `action ${event.type} has already been handled, skipping`);
    return;
  }

  try {
    handler(event.payload as any);
    handledActionIds.add(event.actionId);
  } catch (e) {
    log.i('handleActionEvent', `action ${event.type} failed`, e);
  }
}

export function useActionListener() {
  const visitorId = useGlobalStore((store) => store.settings.visitorId);
  useWSSubscription(actionEvent({ visitorId }), async ({ data }) => {
    handleActionEvent(data);
  });
}

export function useActionLocationListener() {
  const location = useLocation();
  useEffect(() => {
    const locationId = uuidv4();

    const sp = new URLSearchParams(location.search);
    const originalSearch = sp.toString();

    for (const [, action] of Object.entries(ACTIONS)) {
      const payload = action.onSearchParams(sp);
      if (payload != null) {
        handleActionEvent({
          actionId: `${locationId}-${action.type}`,
          type: action.type,
          payload,
        } as ActionEvent);
      }
    }

    const newSearch = sp.toString();
    if (newSearch !== originalSearch) {
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [location.search]);
}
